import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Play, Pause, X, RotateCcw, AlertCircle } from 'lucide-react';
import { ARCHETYPES } from './archetypeData';

const RealityPlayer = ({ state, onClose }) => {
  const archetype = ARCHETYPES[state?.archetypeId];
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [hasVoice, setHasVoice] = useState(false);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);
  const musicRef = useRef(null);
  const progressInterval = useRef(null);

  useEffect(() => {
    // Check if we have a valid voice recording
    if (state?.voiceRecordingUrl && state.voiceRecordingUrl.startsWith('blob:')) {
      // It's a blob URL - we should have it
      setHasVoice(true);
    } else if (state?.voiceRecordingUrl) {
      setHasVoice(true);
    }
    
    return () => {
      if (progressInterval.current) clearInterval(progressInterval.current);
      if (audioRef.current) audioRef.current.pause();
      if (musicRef.current) musicRef.current.pause();
    };
  }, [state]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePlay = async () => {
    setError(null);
    
    if (isPlaying) {
      audioRef.current?.pause();
      musicRef.current?.pause();
      if (progressInterval.current) clearInterval(progressInterval.current);
      setIsPlaying(false);
      return;
    }
    
    try {
      // Play background music first (always available)
      if (musicRef.current && state.musicTrack?.url) {
        musicRef.current.src = state.musicTrack.url;
        musicRef.current.volume = state.settings?.musicVolume || 0.3;
        musicRef.current.loop = true;
        await musicRef.current.play().catch(e => console.log('Music play error:', e));
      }
      
      // Try to play voice recording if available
      if (audioRef.current && state.voiceRecordingUrl) {
        audioRef.current.src = state.voiceRecordingUrl;
        audioRef.current.volume = state.settings?.voiceVolume || 0.7;
        
        try {
          await audioRef.current.play();
        } catch (e) {
          console.log('Voice play error:', e);
          // Voice might not be available, but music can still play
          setHasVoice(false);
        }
      }

      setIsPlaying(true);
      
      // Update progress
      const duration = state.settings?.duration || 180;
      progressInterval.current = setInterval(() => {
        setCurrentTime(prev => {
          const newTime = prev + 1;
          setProgress((newTime / duration) * 100);
          if (newTime >= duration) {
            clearInterval(progressInterval.current);
            setIsPlaying(false);
            if (musicRef.current) musicRef.current.pause();
            return 0;
          }
          return newTime;
        });
      }, 1000);
      
    } catch (err) {
      console.error('Playback error:', err);
      setError('Unable to play audio. Try creating a new recording.');
    }
  };

  const handleRestart = () => {
    setCurrentTime(0);
    setProgress(0);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
    if (musicRef.current) {
      musicRef.current.currentTime = 0;
    }
  };

  return (
    <div className="fixed inset-0 bg-[#0a0a12] z-50 flex flex-col">
      <audio ref={audioRef} onEnded={() => {
        // Voice ended, but keep music playing until duration completes
      }} />
      <audio ref={musicRef} />

      {/* Close button */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        onClick={onClose}
        className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors z-10"
      >
        <X size={20} className="text-white/60" />
      </motion.button>

      {/* Ambient glow */}
      <motion.div
        className="absolute left-1/2 top-1/3 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full pointer-events-none"
        style={{
          background: `radial-gradient(circle, ${archetype?.glowColor || 'rgba(147, 51, 234, 0.3)'} 0%, transparent 70%)`
        }}
        animate={{
          scale: isPlaying ? [1, 1.15, 1] : 1,
          opacity: isPlaying ? [0.4, 0.6, 0.4] : 0.3
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center justify-center px-8">
        {/* State emoji/icon */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-6xl mb-6"
        >
          {archetype?.emoji || '✨'}
        </motion.div>

        {/* State name */}
        <motion.h1
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="text-2xl font-light text-white mb-2 text-center"
        >
          {state?.name || 'My State'}
        </motion.h1>

        {/* Archetype */}
        <motion.p
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-white/40 text-sm mb-12"
        >
          {archetype?.tagline}
        </motion.p>
        
        {/* Error message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 text-amber-400 text-sm mb-6 px-4 py-2 bg-amber-500/10 rounded-lg"
          >
            <AlertCircle size={16} />
            <span>{error}</span>
          </motion.div>
        )}
        
        {/* No voice recording notice */}
        {!hasVoice && !error && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-white/40 text-xs mb-4"
          >
            Playing music only (voice not recorded)
          </motion.p>
        )}

        {/* Progress bar */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="w-full max-w-xs mb-4"
        >
          <div className="h-1 bg-white/10 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-purple-500 to-violet-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between mt-2">
            <span className="text-white/40 text-xs">{formatTime(currentTime)}</span>
            <span className="text-white/40 text-xs">{formatTime(state?.settings?.duration || 180)}</span>
          </div>
        </motion.div>

        {/* Controls */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="flex items-center gap-6"
        >
          <button
            onClick={handleRestart}
            className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
          >
            <RotateCcw size={18} className="text-white/60" />
          </button>

          <button
            onClick={handlePlay}
            className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-violet-500 flex items-center justify-center shadow-lg shadow-purple-500/40 hover:shadow-purple-500/60 transition-shadow"
            data-testid="player-play-btn"
          >
            {isPlaying ? (
              <Pause size={32} className="text-white" />
            ) : (
              <Play size={32} className="text-white ml-1" />
            )}
          </button>

          <div className="w-12 h-12" /> {/* Spacer for symmetry */}
        </motion.div>
      </div>

      {/* Done button - with safe area */}
      <div className="flex-shrink-0 p-6 pb-24 bg-gradient-to-t from-[#0a0a12] via-[#0a0a12] to-transparent">
        <button
          onClick={onClose}
          className="w-full py-4 rounded-2xl bg-white/10 text-white font-medium hover:bg-white/20 transition-colors"
          data-testid="player-done-btn"
        >
          Done
        </button>
        <p className="text-center text-white/30 text-xs mt-3">
          Your state is saved · Find it in Step Into Reality
        </p>
      </div>
    </div>
  );
};

export default RealityPlayer;
